'use client';

import * as React from 'react';
import { motion, useMotionValue, useSpring, type SpringOptions } from 'motion/react';

// Magic UI SmoothCursor — branded gold/obsidian for Moose Ai Legion.
//
// Design: the **core dot tracks the pointer 1:1** (no spring, zero lag —
// uses a raw motion value driven directly by `pointermove`). The
// **trailing ring** still uses a spring so it catches up smoothly with
// physics, giving the branded "smooth" feel without the dot ever falling
// behind the actual mouse position.
//
// Auto-disables on touch devices.

const DEFAULT_RING_SPRING: SpringOptions = {
  damping: 28,
  stiffness: 350,
  mass: 0.8,
  restDelta: 0.001,
};

type SmoothCursorProps = {
  cursor?: React.ReactNode;
  springConfig?: SpringOptions;
};

export function SmoothCursor({ cursor, springConfig = DEFAULT_RING_SPRING }: SmoothCursorProps) {
  const [isTouch, setIsTouch] = React.useState(false);
  const [visible, setVisible] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);
  const [hoveringClickable, setHoveringClickable] = React.useState(false);
  // When over a react-grid-layout resize handle we step out of the way
  // and let the native nwse-/nesw-resize cursor show through. Otherwise
  // operators couldn't see which way they were pulling.
  const [overResizeHandle, setOverResizeHandle] = React.useState(false);

  // Core dot — direct motion value, no spring. Set straight from the
  // pointermove event for true real-time tracking.
  const x = useMotionValue(-50);
  const y = useMotionValue(-50);
  // Trailing ring — soft spring so it lags the dot with physics.
  const trailX = useSpring(x, springConfig);
  const trailY = useSpring(y, springConfig);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const touch = window.matchMedia('(pointer: coarse)').matches;
    if (touch) {
      setIsTouch(true);
      return;
    }

    // Seed at viewport center so it's visible immediately (we hid the
    // native cursor — without a seed the user sees "no cursor at all"
    // until the first pointermove).
    const seedX = window.innerWidth / 2;
    const seedY = window.innerHeight / 2;
    x.set(seedX);
    y.set(seedY);
    setVisible(true);

    const onMove = (e: PointerEvent) => {
      x.set(e.clientX);
      y.set(e.clientY);
      if (!visible) setVisible(true);

      const target = e.target as HTMLElement | null;
      if (target) {
        const onResize =
          target.closest('.react-resizable-handle') !== null ||
          target.closest('[data-resize-handle]') !== null;
        setOverResizeHandle(onResize);
        const interactive =
          target.closest('a, button, [role="button"], input, textarea, select, [data-clicky], label') !== null;
        setHoveringClickable(interactive);
      }
    };
    const onDown = () => setPressed(true);
    const onUp = () => setPressed(false);
    const onLeave = () => setVisible(false);
    const onEnter = () => setVisible(true);

    // Use capture + passive for the lowest-latency hookup possible.
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerdown', onDown, { passive: true });
    window.addEventListener('pointerup', onUp, { passive: true });
    window.addEventListener('pointerleave', onLeave, { passive: true });
    window.addEventListener('pointerenter', onEnter, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointerleave', onLeave);
      window.removeEventListener('pointerenter', onEnter);
    };
  }, [x, y, visible]);

  if (isTouch) return null;

  // Custom cursor escape hatch — bypass our branded look entirely.
  if (cursor) {
    return (
      <motion.div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          x,
          y,
          translateX: '-50%',
          translateY: '-50%',
          pointerEvents: 'none',
          zIndex: 2147483647,
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.2s ease',
        }}
      >
        {cursor}
      </motion.div>
    );
  }

  // Brand defaults — gold dot + soft trailing ring + click flash.
  const dotSize = pressed ? 8 : 10;
  const ringSize = hoveringClickable ? 36 : 26;

  // Effective visibility — also hide when over a resize handle so the
  // native diagonal-resize cursor reads cleanly.
  const showCursor = visible && !overResizeHandle;

  return (
    <>
      {/* Trailing ring */}
      <motion.div
        aria-hidden
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          x: trailX,
          y: trailY,
          translateX: '-50%',
          translateY: '-50%',
          width: ringSize,
          height: ringSize,
          borderRadius: 999,
          border: `1px solid ${hoveringClickable ? 'rgba(232, 193, 106, 0.85)' : 'rgba(232, 193, 106, 0.45)'}`,
          background: hoveringClickable
            ? 'radial-gradient(circle, rgba(232, 193, 106, 0.12) 0%, rgba(232, 193, 106, 0) 70%)'
            : 'transparent',
          pointerEvents: 'none',
          zIndex: 2147483646,
          opacity: showCursor ? 1 : 0,
          transition: 'opacity 0.2s ease, width 0.18s ease, height 0.18s ease, border-color 0.18s ease, background 0.18s ease',
          boxShadow: hoveringClickable
            ? '0 0 24px rgba(232, 193, 106, 0.35)'
            : '0 0 12px rgba(232, 193, 106, 0.18)',
        }}
      />
      {/* Core dot */}
      <motion.div
        aria-hidden
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          x,
          y,
          translateX: '-50%',
          translateY: '-50%',
          width: dotSize,
          height: dotSize,
          borderRadius: 999,
          background: 'linear-gradient(135deg, #F6E6B8 0%, #E8C16A 35%, #B8862E 90%)',
          boxShadow: '0 0 14px rgba(232, 193, 106, 0.85), 0 0 4px rgba(232, 193, 106, 1)',
          pointerEvents: 'none',
          zIndex: 2147483647,
          opacity: showCursor ? 1 : 0,
          transition: 'opacity 0.18s ease, width 0.12s ease, height 0.12s ease',
        }}
      />
    </>
  );
}
